import axios from 'axios'
import moment from 'moment'
import { buildIndex } from '@adalo/utils'

import { baseURL } from '../../utils/io'

const FETCH_ORGANIZATIONS = 'FETCH_ORGANIZATIONS'
const FETCH_ORGANIZATION = 'FETCH_ORGANIZATION'
const UPDATE_ORGANIZATION = 'UPDATE_ORGANIZATION'
const CREATE_INVITE = 'CREATE_INVITE'
const DELETE_INVITE = 'DELETE_INVITE'
const REMOVE_USER = 'REMOVE_USER'
const START_INTEGRATION_TRIAL_FULFILLED = 'START_INTEGRATION_TRIAL_FULFILLED'
const SET_TRIAL_AS_OVER = 'SET_TRIAL_AS_OVER'
export const SET_CURRENT_ORGANIZATION = 'SET_CURRENT_ORGANIZATION'

export const INITIAL_STATE = {
  organizations: {},
  currentOrganization: null,
}

// Reducer
function setTrialState(state, canStartTrial, isTrialActive, trialEndTimestamp) {
  const id = state.currentOrganization
  const organizations = { ...state.organizations }

  if (organizations[id].trialState) {
    organizations[id].trialState = {
      ...organizations[id].trialState,
      canStartTrial,
      isTrialActive,
      trialEndTimestamp,
    }

    return {
      ...state,
      organizations,
    }
  }

  return state
}

export default (state = INITIAL_STATE, action) => {
  if (action?.type === START_INTEGRATION_TRIAL_FULFILLED) {
    const { trialEndTimestamp } = action?.payload?.data

    return setTrialState(state, false, true, trialEndTimestamp)
  }

  if (action?.type === SET_TRIAL_AS_OVER) {
    return setTrialState(state, false, false)
  }

  if (action?.type === `${FETCH_ORGANIZATIONS}_FULFILLED`) {
    const organizations = buildIndex(action.payload.data, org => {
      return org.id
    })

    return {
      ...state,
      organizations,
    }
  }

  if (action?.type === `${UPDATE_ORGANIZATION}_FULFILLED`) {
    const organization = action.payload.data

    return {
      ...state,
      organizations: {
        ...state.organizations,
        [organization.id]: {
          ...state.organizations[organization.id],
          ...organization,
        },
      },
    }
  }

  if (action?.type === `${SET_CURRENT_ORGANIZATION}`) {
    const { payload: organizationId } = action

    return {
      ...state,
      currentOrganization: organizationId,
    }
  }

  if (action?.type === `${FETCH_ORGANIZATION}_FULFILLED`) {
    const { payload } = action
    const id = payload.data.id
    const organizations = { ...state.organizations }

    organizations[id] = {
      ...state.organizations[id],
      ...payload.data,
    }

    return {
      ...state,
      organizations,
    }
  }

  return state
}

// Actions
export const setTrialAsOver = () => ({
  type: SET_TRIAL_AS_OVER,
})

export const fetchOrganizations = () => ({
  type: FETCH_ORGANIZATIONS,
  payload: axios.get(`${baseURL}/organizations`),
})

export const fetchOrganization = id => ({
  type: FETCH_ORGANIZATION,
  payload: axios.get(`${baseURL}/organizations/${id}`),
})

export const updateOrganization = (id, organization) => ({
  type: UPDATE_ORGANIZATION,
  payload: axios.put(`${baseURL}/organizations/${id}`, organization),
})

export const createInvite = ({ email, organizationId, appId }) => ({
  type: CREATE_INVITE,
  payload: axios.post(`${baseURL}/invitations`, {
    email,
    organizationId,
    appId,
  }),
})

export const removeUser = ({ organizationId, userId }) => ({
  type: REMOVE_USER,
  payload: axios.delete(
    `${baseURL}/organizations/${organizationId}/users/${userId}`
  ),
})

export const deleteInvite = inviteId => ({
  type: DELETE_INVITE,
  payload: axios.delete(`${baseURL}/invitations/${inviteId}`),
})

export const setCurrentOrganization = organizationId => ({
  type: 'SET_CURRENT_ORGANIZATION',
  payload: organizationId,
})

export const setDefaultCurrentOrganization = organizationId => {
  return (dispatch, getState) => {
    const { currentOrganization } = getState()

    if (!currentOrganization) {
      dispatch(setCurrentOrganization(organizationId))
    }
  }
}

export const getOrganizationLastApp = async (organizationId, userId) => {
  try {
    const response = await axios.get(
      `${baseURL}/organizations/${organizationId}/users/${userId}/last-app`
    )

    return response.data
  } catch (error) {
    console.error(error)
  }
}
// Selectors

export const getOrganizations = state => {
  const organizations = Object.values(state.organizations.organizations)

  if (organizations) {
    return organizations.sort((a, b) => a.name.localeCompare(b.name))
  }

  return []
}

export const getOrganization = (state, orgId) => {
  return state.organizations.organizations[orgId]
}

export const getCurrentOrganization = state => {
  const currOrgId = state.organizations?.currentOrganization

  let orgs = state?.organizations?.organizations
    ? Object.values(state.organizations.organizations)
    : null

  if (!orgs) return null

  if (orgs.length > 1) {
    orgs = orgs.filter(org => {
      return org.id === currOrgId
    })
  }

  return orgs[0]
}

export const getTrialRemainingTime = state => {
  const organization = getCurrentOrganization(state)
  const trialState = organization?.trialState

  if (!trialState) {
    return 0
  }

  const { trialEndTimestamp } = trialState

  const end = moment(trialEndTimestamp)
  const now = moment()
  const remainingTime = end.diff(now).valueOf()

  return remainingTime
}

export const getTrialState = state => {
  const organization = getCurrentOrganization(state)
  const trialState = organization?.trialState

  if (!trialState) {
    return {}
  }

  let days = null
  let trialStateString = 'before'

  const {
    canStartTrial,
    isTrialActive,
    trialStartTimestamp,
    trialEndTimestamp,
  } = trialState

  if (!canStartTrial) {
    if (isTrialActive) {
      trialStateString = 'during'
      const start = moment(trialStartTimestamp)
      const end = moment(trialEndTimestamp)
      days = moment.duration(end.diff(start)).asDays().toFixed()
    } else {
      trialStateString = 'after'
    }
  }

  return {
    trialState: trialStateString,
    days,
    trialEndTimestamp,
  }
}

export const getEnabledFeatures = state => {
  const organization = getCurrentOrganization(state)
  const enabledFeatures = organization?.enabledFeatures

  return enabledFeatures || []
}

export const isFeatureEnabled = (state, feature) => {
  const organization = getCurrentOrganization(state)
  const enabledFeatures = organization?.enabledFeatures

  return enabledFeatures?.includes(feature)
}

export const getActiveState = state => {
  const organization = getCurrentOrganization(state)
  const active = organization?.active

  return active
}

export const getCurrentOrganizationId = state => {
  return state.organizations?.currentOrganization
}

export const getCurrentOrgInitialDetails = state => {
  const colors = ['teal', 'orange', 'yellow']
  const currentOrganization = getCurrentOrganization(state)
  const organizations = getOrganizations(state)

  let orgInitials = 'A'
  let orgIndex = 0

  if (currentOrganization) {
    if (currentOrganization.name) {
      orgInitials = getOrgInitials(currentOrganization.name)
    }

    if (organizations) {
      orgIndex = organizations.findIndex(
        org => org.id === currentOrganization.id
      )
    }
  }

  const color = colors[orgIndex % colors.length]

  return { orgInitials, orgColor: color }
}

export const getOrgInitials = name => {
  name = name
    .split(/\s/)
    .reduce((response, word) => (response += word.slice(0, 1)), '')
    .toUpperCase()

  return name.slice(0, 2)
}
